import axios from 'axios';
import { useEffect, useRef } from 'react';
import {
  UserAgentUtils,
  logger,
  compareAppVersions,
  isValidAppVersion,
} from '../../utils';
import packageJson from '../../../package.json';
import { toast } from 'react-toastify';
import { useLocation } from 'react-router-dom';
import { ChangelogApi } from '../../configs';
import { ActionCommon, useAppDispatch, useAppSelector,  VRIAppStateType } from '../../core';

const VERSION_TOAST_ID = 'app-version-notice';

export function useVersionCheck(skipPath: 'login' | '' = '') {
  const { pathname } = useLocation();
  const isFirstLoadRef = useRef(true);
  const dispatch = useAppDispatch();
  const isBackdatedVersion = useAppSelector(
    (state:  VRIAppStateType) => state.common.isBackdatedVersion
  );
  const isUpdateAvailable = useAppSelector(
    (state: VRIAppStateType) => state.common.isUpdateAvailable
  );

  useEffect(() => {
    if (!isFirstLoadRef.current) {
      return;
    }
    const checkVersion = async () => {
      try {
        const response = await axios.get(ChangelogApi.Changelog.LatestVersion());
        if (response.status !== 200 || !response.data) {
          return;
        }
        const latestVersion = response.data.version;
        const minimumVersion = response.data.minimumVersion;
        if (!isValidAppVersion(latestVersion)) {
          return;
        }
        const currentVersion = packageJson.version;
        logger({ message: 'Current App version: ', data: currentVersion });
        logger({ message: 'Latest App version: ', data: latestVersion });
        logger({ message: 'Minimum App version: ', data: minimumVersion });

        const isBelowMinimum =
          isValidAppVersion(minimumVersion) &&
          compareAppVersions(currentVersion, minimumVersion) < 0;
        const isBelowLatest =
          compareAppVersions(currentVersion, latestVersion) < 0;

        dispatch(ActionCommon.setIsBackdatedVersion(isBelowMinimum));
        dispatch(
          ActionCommon.setIsUpdateAvailable(!isBelowMinimum && isBelowLatest)
        );
      } catch (error) {
        // "Can't check" is never a reason to block anyone.
        logger({ message: 'Error: ', data: error });
      }
    };
    if (!skipPath || (skipPath && !pathname.includes(skipPath))) {
      const oneHour = 3600000;
      checkVersion();
      const intervalId = setInterval(() => {
        checkVersion();
      }, oneHour);
      isFirstLoadRef.current = false;

      return () => {
        clearInterval(intervalId);
      };
    }
  }, [skipPath, pathname, dispatch]);

  useEffect(() => {
    if (isBackdatedVersion || isUpdateAvailable) {
      let platformMessage = '';
      let endMessage = pathname.includes('login')
        ? 'proceed further'
        : 'update';
      if (UserAgentUtils.isElectron() || UserAgentUtils.isWebView()) {
        platformMessage = `Please quit the app completely an reopen to ${endMessage}`;
      } else {
        platformMessage = `Please refresh the web page to ${endMessage}`;
      }
      const content = `A newer version of Gateway To Access is available. ${platformMessage}.`;
      // A stable toastId + update-in-place avoids react-toastify's
      // double-dismiss crash (deleteToast reading props of undefined).
      if (toast.isActive(VERSION_TOAST_ID)) {
        toast.update(VERSION_TOAST_ID, { render: content });
      } else {
        toast(content, {
          toastId: VERSION_TOAST_ID,
          position: 'top-center',
          autoClose: false,
          bodyStyle: {
            background: 'white',
            color: '#272829',
          },
        });
      }
    } else if (toast.isActive(VERSION_TOAST_ID)) {
      toast.dismiss(VERSION_TOAST_ID);
    }
  }, [isBackdatedVersion, isUpdateAvailable, pathname]);

  return {
    isBackdatedVersion,
    isUpdateAvailable,
  };
}
